package com.karyadina.ui.screens.flashcards.components

import androidx.compose.animation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ChevronRight
import androidx.compose.material.icons.filled.FolderOpen
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.karyadina.model.FlashCard_Subject
import com.karyadina.model.Unit as StudyUnit
import com.karyadina.service.FlashcardService
import com.karyadina.ui.components.StudyExtendedFloatingActionButton

// TODO: Refactor service calls to use ViewModel entirely in Phase 3
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SubjectDetailScreen(
    subject: FlashCard_Subject,
    service: FlashcardService,
    onBack: () -> Unit,
    onSelectUnit: (StudyUnit) -> Unit = {}
) {
    var units by remember { mutableStateOf(service.getUnits(subject.id)) }
    var showAddUnitDialog by remember { mutableStateOf(false) }
    val listState = androidx.compose.foundation.lazy.rememberLazyListState()
    val fabVisible by remember {
        androidx.compose.runtime.derivedStateOf {
            listState.firstVisibleItemIndex == 0
        }
    }
    
    // Use Scaffold for FAB (parent uses Box, so no nesting conflict)
    Scaffold(
        floatingActionButton = {
            // Show FAB when units exist and at top of list
            androidx.compose.animation.AnimatedVisibility(
                visible = units.isNotEmpty() && fabVisible,
                enter = androidx.compose.animation.slideInVertically(
                    initialOffsetY = { it * 2 }
                ) + androidx.compose.animation.fadeIn(),
                exit = androidx.compose.animation.slideOutVertically(
                    targetOffsetY = { it * 2 }
                ) + androidx.compose.animation.fadeOut()
            ) {
                StudyExtendedFloatingActionButton(
                    onClick = { showAddUnitDialog = true },
                    icon = { Icon(Icons.Filled.Add, "Add") },
                    text = { Text("Add Unit") }
                )
            }
        }
    ) { padding ->
        if (units.isEmpty()) {
            EmptyUnitsState(
                subjectName = subject.name,
                onAddUnit = { showAddUnitDialog = true },
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding)
            )
        } else {
            LazyColumn(
                state = listState,
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                items(units) { unit ->
                    UnitCard(
                        unit = unit,
                        subjectColor = subject.color,
                        onClick = { onSelectUnit(unit) },
                        onDelete = {
                            service.deleteUnit(unit.id)
                            units = service.getUnits(subject.id)
                        }
                    )
                }
            }
        }
    }
    
    if (showAddUnitDialog) {
        AddUnitDialog(
            subjectName = subject.name,
            nextOrder = units.size + 1,
            onDismiss = { showAddUnitDialog = false },
            onAdd = { name ->
                service.createUnit(subject.id, name, units.size + 1)
                units = service.getUnits(subject.id)
                showAddUnitDialog = false
            }
        )
    }
}

@Composable
fun UnitCard(
    unit: StudyUnit,
    subjectColor: String,
    onClick: () -> Unit,
    onDelete: () -> Unit
) {
    var showDeleteDialog by remember { mutableStateOf(false) }
    
    ElevatedCard(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.dp),
                modifier = Modifier.weight(1f)
            ) {
                Surface(
                    modifier = Modifier.size(40.dp),
                    shape = CircleShape,
                    color = com.karyadina.ui.theme.StudyTheme.parseHexColor(subjectColor).copy(alpha = 0.2f)
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Text(
                            unit.order.toString(),
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = com.karyadina.ui.theme.StudyTheme.parseHexColor(subjectColor)
                        )
                    }
                }
                
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        unit.name,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        "${unit.masteredCards}/${unit.totalCards} cards mastered",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                    )
                }
            }
            
            Row(
                horizontalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                IconButton(onClick = { showDeleteDialog = true }) {
                    Icon(Icons.Outlined.Delete, "Delete", tint = MaterialTheme.colorScheme.error)
                }
                Icon(
                    Icons.Filled.ChevronRight,
                    contentDescription = null,
                    modifier = Modifier.size(24.dp)
                )
            }
        }
    }
    
    if (showDeleteDialog) {
        AlertDialog(
            onDismissRequest = { showDeleteDialog = false },
            title = { Text("Delete Unit?") },
            text = { Text("This will delete all flashcards in this unit. This action cannot be undone.") },
            confirmButton = {
                TextButton(
                    onClick = {
                        onDelete()
                        showDeleteDialog = false
                    },
                    colors = ButtonDefaults.textButtonColors(
                        contentColor = MaterialTheme.colorScheme.error
                    )
                ) {
                    Text("Delete")
                }
            },
            dismissButton = {
                TextButton(onClick = { showDeleteDialog = false }) {
                    Text("Cancel")
                }
            }
        )
    }
}

@Composable
fun AddUnitDialog(
    subjectName: String,
    nextOrder: Int,
    onDismiss: () -> Unit,
    onAdd: (String) -> Unit
) {
    var name by remember { mutableStateOf("") }
    
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Add Unit to $subjectName") },
        text = {
            OutlinedTextField(
                value = name,
                onValueChange = { name = it },
                label = { Text("Unit/Chapter Name") },
                placeholder = { Text("e.g., Unit $nextOrder: Calculus") },
                singleLine = true,
                modifier = Modifier.fillMaxWidth()
            )
        },
        confirmButton = {
            Button(
                onClick = {
                    if (name.isNotBlank()) {
                        onAdd(name)
                    }
                },
                enabled = name.isNotBlank()
            ) {
                Text("Add")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        }
    )
}

@Composable
fun EmptyUnitsState(
    subjectName: String,
    onAddUnit: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .padding(32.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Icon(
            Icons.Filled.FolderOpen,
            contentDescription = null,
            modifier = Modifier.size(100.dp),
            tint = MaterialTheme.colorScheme.primary.copy(alpha = 0.3f)
        )
        Spacer(modifier = Modifier.height(24.dp))
        Text(
            "No Units Yet",
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold
        )
        Spacer(modifier = Modifier.height(8.dp))
        Text(
            "Add units or chapters for $subjectName",
            style = MaterialTheme.typography.bodyLarge,
            textAlign = TextAlign.Center,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
        )
        Spacer(modifier = Modifier.height(32.dp))
        StudyExtendedFloatingActionButton(
            onClick = onAddUnit,
            icon = { Icon(Icons.Filled.Add, "Add") },
            text = { Text("Add Unit") }
        )
    }
}
